import type { IssueReservedViewDataConfig } from '@trimble/tc-issue-management-ui';
import type { TcRegion, TcTcpsApiService } from '@data-access/connect/tc-tcps';
import type { ScanDataApiService } from '@data-access/geospatial/scan-data';
import type {
  ActiveItems,
  FeatureExtractionContextType,
  ProjectDataState,
} from '@mobile-mapping/providers';
import type { ViewMode } from '@mobile-mapping/web3d';
import type { CapabilityRegistry } from '@trimble/web3d';
import type { PaneLayouts } from '@trimble/pane-layout';
import type { ILogger } from '@trimble/logging';
import { asError } from '@trimble/logging';
import { t } from '@trimble/i18n';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import {
  buildNumarkReservedData,
  type NumarkReservedSnapshot,
} from '../views/build-numark-reserved-data';

export type CreateNumarkIssueReservedViewDataConfigDeps = {
  readonly tcTcpsApi: Pick<TcTcpsApiService, 'getViewDetails' | 'updateView'>;
  readonly scanDataApi: Pick<ScanDataApiService, 'savePointCloudsForView'>;
  getRegion(): TcRegion | undefined;
  getProjectId(): string | undefined;
  readonly capabilityRegistry: CapabilityRegistry;
  readonly layouts: PaneLayouts<ViewMode>;
  readonly activeItems: ActiveItems;
  readonly dataState: ProjectDataState;
  readonly featureExtractionContext: FeatureExtractionContextType;
  readonly logger: ILogger;
};

export type NumarkIssueReservedViewDataConfig = {
  readonly reservedViewDataConfig: IssueReservedViewDataConfig;
  pushSnapshot(issueId: string, snapshot: NumarkReservedSnapshot): void;
  beginCreateCapture(): number;
  pushCreateSnapshot(
    snapshot: NumarkReservedSnapshot,
    generation: number
  ): boolean;
  popLastSnapshot(issueId: string): void;
  clearSnapshots(issueId: string): void;
};

export function createNumarkIssueReservedViewDataConfig(
  deps: CreateNumarkIssueReservedViewDataConfigDeps
): NumarkIssueReservedViewDataConfig {
  const pendingSnapshotsByIssueId = new Map<string, NumarkReservedSnapshot[]>();
  let pendingCreateSnapshot: NumarkReservedSnapshot | undefined;
  let createCaptureGeneration = 0;

  const pushSnapshot = (
    issueId: string,
    snapshot: NumarkReservedSnapshot
  ): void => {
    const queue = pendingSnapshotsByIssueId.get(issueId) ?? [];
    queue.push(snapshot);
    pendingSnapshotsByIssueId.set(issueId, queue);
  };

  const beginCreateCapture = (): number => {
    createCaptureGeneration += 1;
    return createCaptureGeneration;
  };

  const pushCreateSnapshot = (
    snapshot: NumarkReservedSnapshot,
    generation: number
  ): boolean => {
    if (generation !== createCaptureGeneration) {
      return false;
    }
    pendingCreateSnapshot = snapshot;
    return true;
  };

  const popLastSnapshot = (issueId: string): void => {
    const queue = pendingSnapshotsByIssueId.get(issueId);
    if (!queue?.length) {
      return;
    }
    queue.pop();
    if (queue.length === 0) {
      pendingSnapshotsByIssueId.delete(issueId);
    } else {
      pendingSnapshotsByIssueId.set(issueId, queue);
    }
  };

  const clearSnapshots = (issueId: string): void => {
    pendingSnapshotsByIssueId.delete(issueId);
  };

  const takeNextSnapshot = (
    issueId: string | undefined
  ): NumarkReservedSnapshot | undefined => {
    if (!issueId) {
      return undefined;
    }
    const queue = pendingSnapshotsByIssueId.get(issueId);
    const snapshot = queue?.shift();
    if (queue && queue.length === 0) {
      pendingSnapshotsByIssueId.delete(issueId);
    }
    return snapshot;
  };

  const takePendingCreateSnapshot = (): NumarkReservedSnapshot | undefined => {
    const snapshot = pendingCreateSnapshot;
    pendingCreateSnapshot = undefined;
    return snapshot;
  };

  const savePreviewViewAssociations = async (
    viewId: string,
    snapshot: NumarkReservedSnapshot,
    region: TcRegion
  ): Promise<void> => {
    const projectId = deps.getProjectId();
    if (!projectId) {
      throw new Error('No project selected');
    }

    const markups =
      snapshot.markups.length > 0 ? [...snapshot.markups] : undefined;

    if (markups) {
      try {
        await deps.tcTcpsApi.updateView(region, viewId, { markups });
      } catch (error) {
        deps.logger.error(
          'Failed to patch preview view',
          { viewId },
          asError(error)
        );
        throw error instanceof Error ? error : new Error(String(error));
      }
    }

    try {
      await deps.scanDataApi.savePointCloudsForView(region, projectId, viewId, [
        ...(snapshot.pointCloudIds ?? []),
      ]);
    } catch (error) {
      deps.logger.error(
        'Failed to save point clouds for preview view',
        { viewId },
        asError(error)
      );
      throw error instanceof Error ? error : new Error(String(error));
    }
  };

  const consumePendingSnapshotAndBuild = async ({
    viewId,
    issueId,
  }: {
    viewId: string;
    issueId?: string;
  }): Promise<Record<string, unknown>> => {
    const region = deps.getRegion();
    const queued = takeNextSnapshot(issueId);
    const preview = queued ? undefined : takePendingCreateSnapshot();
    const snapshot = queued ?? preview;

    if (!region) {
      if (snapshot) {
        throw new Error('No region selected');
      }
      return {};
    }

    if (!snapshot) {
      return {};
    }

    if (preview) {
      await savePreviewViewAssociations(viewId, preview, region);
    }

    try {
      return await buildNumarkReservedData(
        {
          tcTcpsApi: deps.tcTcpsApi,
          region,
          capabilityRegistry: deps.capabilityRegistry,
          layouts: deps.layouts,
          activeItems: deps.activeItems,
          dataState: deps.dataState,
          featureExtractionContext: deps.featureExtractionContext,
        },
        viewId,
        snapshot
      );
    } catch (error) {
      deps.logger.error(
        'Failed to build numark reserved data',
        {
          viewId,
          issueId,
        },
        asError(error)
      );
      throw error instanceof Error ? error : new Error(String(error));
    }
  };

  const reservedViewDataConfig: IssueReservedViewDataConfig = {
    appName: 'numark',
    getReservedData: consumePendingSnapshotAndBuild,
    onReservedDataError: (viewId: string, error: Error) => {
      pendingCreateSnapshot = undefined;
      deps.logger.warn(
        `Multipane state save failed for view ${viewId}`,
        undefined,
        asError(error)
      );
      NotificationEventBus.emit({
        type: 'warning',
        message: t('mobileMapping.views.create.multipaneStateSaveFailed'),
      });
    },
  };

  return {
    reservedViewDataConfig,
    pushSnapshot,
    beginCreateCapture,
    pushCreateSnapshot,
    popLastSnapshot,
    clearSnapshots,
  };
}
